// src/hooks/useBoardPackJobs.js
// Background finalize / export jobs of the selected board pack: the one still
// running (so the buttons show its progress instead of queuing another) and
// the last export that is ready to download.
import { useCallback, useEffect, useState } from "react";
import { apiCall } from "@/api/apiCall";
import { FINISHED_STATUSES, JOBS_CHANGED_EVENT, JOBS_UPDATED_EVENT } from "@/lib/reportJobs";

const POLL_MS = 5000;

export const useBoardPackJobs = (contract, year, month) => {
  const [jobs, setJobs] = useState([]);

  const load = useCallback(async () => {
    if (!contract || !year || !month) return;
    try {
      const res = await apiCall(
        "get",
        "/reports/jobs",
        { contract, year, month, limit: 20 },
        null,
        false,
        false
      );
      if (res?.success && Array.isArray(res.data)) setJobs(res.data);
    } catch {
      /* keep the last state */
    }
  }, [contract, year, month]);

  useEffect(() => {
    // fetch on selection change; state is only set after the response arrives
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  // A request was just sent or the watcher saw a change
  useEffect(() => {
    window.addEventListener(JOBS_CHANGED_EVENT, load);
    window.addEventListener(JOBS_UPDATED_EVENT, load);
    return () => {
      window.removeEventListener(JOBS_CHANGED_EVENT, load);
      window.removeEventListener(JOBS_UPDATED_EVENT, load);
    };
  }, [load]);

  // Ignore results of a previously selected board pack
  const current = contract
    ? jobs.filter((job) => job.contract === contract && job.year === year && job.month === month)
    : [];
  const isActive = (job) => !FINISHED_STATUSES.includes(job.status);
  const activeExport = current.find((job) => job.job_type === "export" && isActive(job)) || null;
  const activeFinalize = current.find((job) => job.job_type !== "export" && isActive(job)) || null;
  const latestExport =
    current.find(
      (job) => job.job_type === "export" && job.status === "done" && (job.has_pdf || job.has_pptx)
    ) || null;

  // Poll only while something of this board pack is running
  const running = Boolean(activeExport || activeFinalize);
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [running, load]);

  return { activeExport, activeFinalize, latestExport, reload: load };
};
